//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import Avatar from '@components/atoms/avatar/Avatar';
import { Caption } from '@components/atoms/caption/Caption';
import { BodyText } from '@components/atoms/text/Text';
import { SocialNetwork } from '@models/SocialNetwork';
import { UIACaptionLink } from '@uia/details/captions/UIACaptionLink';
import { SocialUserLink } from '@uia/rights/SocialUserLink';
import { isDefined } from '@utils/filterPredicates';
import { staticRelativeTime } from '@utils/staticRelativeTime';

export interface ContentBadgeProps {
  avatarUrl?: string;
  caption?: string;
  networkUsername: string;
  postedTime: number | undefined;
  socialNetwork: SocialNetwork;
}

export const ContentBadge = ({
  avatarUrl,
  caption,
  networkUsername,
  postedTime,
  socialNetwork,
}: ContentBadgeProps) => (
  <Badge>
    <AvatarWrapper>
      <StyledAvatar imageUrl={avatarUrl} size={50} username={networkUsername} />
    </AvatarWrapper>
    <Content>
      <Header>
        <SocialUserLink
          socialNetwork={socialNetwork}
          username={networkUsername}
        />
        {isDefined(postedTime) && postedTime > 0 && (
          <BodyText>{staticRelativeTime(postedTime, true)}</BodyText>
        )}
      </Header>
      {isDefined(caption) && (
        <Caption
          caption={caption}
          customTextDecorator={(text) =>
            UIACaptionLink({ text, socialNetwork })
          }
          shouldDecorateLinks={true}
          socialNetwork={socialNetwork}
        />
      )}
    </Content>
  </Badge>
);

const Badge = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: start;
    align-items: start;
    gap: ${theme.spacings[2]};
    padding: ${theme.spacings[2]};
    border: ${theme.borderWidths[1]} solid ${theme.colors.gray2};
    border-radius: ${theme.borderRadii[1]};
    background-color: ${theme.colors.gray00};
    box-shadow: ${theme.boxShadows[1]};
  `,
);

const AvatarWrapper = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[1]};
  `,
);

const StyledAvatar = styled(Avatar)(
  ({ theme }) => `
    border-radius: ${theme.borderRadii[2]};
  `,
);

const Content = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    max-height: ${theme.smallMeasure};
    overflow-y: auto;
  `,
);

const Header = styled.div(
  () => `
    display: flex;
    justify-content: space-between;
  `,
);
